import { useCallback, useEffect, useState } from 'react';
import {
  MessageCircle, Send, Loader2, AlertCircle, X, ArrowLeft, RefreshCw,
  Search, UserCheck, Lock, Unlock, AlertTriangle, Clock, CheckCircle,
} from 'lucide-react';
import {
  listAdminTickets, getAdminTicket, staffReply, updateTicket, ticketImgSrc,
  type AdminTicketRow, type TicketDetail, type TicketStatus, type TicketPriority,
} from '@/shared/services/ticketsService';
import { useToast } from '@/shared/contexts/ToastContext';
import { DataTable } from '@/shared/components/ui/DataTable';

const STATUS_META: Record<string, { label: string; color: string; bg: string }> = {
  open:     { label: 'Aberto',     color: '#facc15', bg: 'rgba(250,204,21,0.15)' },
  answered: { label: 'Respondido', color: '#00e1d7', bg: 'rgba(0,225,215,0.15)' },
  closed:   { label: 'Fechado',    color: '#94a3b8', bg: 'rgba(148,163,184,0.15)' },
};

const PRIORITY_META: Record<string, { label: string; color: string }> = {
  low:    { label: 'BAIXA', color: '#64748b' },
  normal: { label: 'NORMAL', color: '#22c55e' },
  high:   { label: 'ALTA', color: '#ef4444' },
};

export default function TicketsPage() {
  const { notify } = useToast();
  const [items, setItems] = useState<AdminTicketRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<TicketStatus | ''>('');
  const [qFilter, setQFilter] = useState('');
  const [selected, setSelected] = useState<TicketDetail | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await listAdminTickets({
        limit: 200,
        status: statusFilter || undefined,
        q: qFilter || undefined,
      });
      setItems(r.items);
      setTotal(r.total);
    } catch (err: any) {
      setError(err?.detail || err?.message || 'Erro');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, qFilter]);

  useEffect(() => { fetchList(); }, [fetchList]);

  if (selected) {
    return (
      <TicketDetailView
        ticket={selected}
        onBack={() => { setSelected(null); fetchList(); }}
        onUpdate={setSelected}
        onReload={fetchList}
      />
    );
  }

  const counts = {
    open:     items.filter(i => i.status === 'open').length,
    answered: items.filter(i => i.status === 'answered').length,
    closed:   items.filter(i => i.status === 'closed').length,
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
            <MessageCircle className="text-[#00e1d7]" size={26} />
            Tickets de Suporte
          </h1>
          <p className="text-sm text-white/65 mt-1">
            {total} ticket{total !== 1 ? 's' : ''} ·{' '}
            <span className="text-yellow-400 font-semibold">{counts.open} aberto{counts.open !== 1 ? 's' : ''}</span>
            {' · '}
            <span className="text-[#00e1d7] font-semibold">{counts.answered} respondido{counts.answered !== 1 ? 's' : ''}</span>
            {' · '}
            <span className="text-white/55">{counts.closed} fechado{counts.closed !== 1 ? 's' : ''}</span>
          </p>
        </div>
        <button
          onClick={fetchList} disabled={loading}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                     bg-[#00e1d7]/10 border border-[#00e1d7]/25 text-[#00e1d7]
                     hover:bg-[#00e1d7]/20 transition cursor-pointer disabled:opacity-50"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Atualizar
        </button>
      </div>

      {/* Filtros */}
      <div className="surface-card rounded-xl p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-2">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)}
            className="bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white">
            <option value="">Todos os status</option>
            <option value="open">Abertos</option>
            <option value="answered">Respondidos</option>
            <option value="closed">Fechados</option>
          </select>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input value={qFilter} onChange={(e) => setQFilter(e.target.value)}
              placeholder="Buscar no assunto..."
              className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg pl-9 pr-3 py-2 text-sm text-white" />
          </div>
        </div>
      </div>

      {error && (
        <div className="surface-card rounded-xl p-4 mb-4 flex items-start gap-3 border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      <DataTable<AdminTicketRow>
        columns={[
          { key: 'status', label: 'Status', width: '120px',
            cell: (t) => {
              const sm = STATUS_META[t.status];
              return (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded"
                      style={{ background: sm.bg, color: sm.color }}>
                  {sm.label.toUpperCase()}
                </span>
              );
            } },
          { key: 'subject', label: 'Ticket', width: '1fr',
            cell: (t) => (
              <div className="min-w-0">
                <p className="text-sm font-bold text-white truncate">#{t.id} — {t.subject}</p>
                <p className="text-[11px] text-white/55 mt-0.5 truncate">
                  De: <span className="text-white/85">{t.accountId || `UserNum ${t.userNum}`}</span>
                  {' · '}{t.messageCount} msg
                </p>
              </div>
            ) },
          { key: 'priority', label: 'Prioridade', width: '110px', mobileHidden: true,
            cell: (t) => t.priority !== 'normal' ? (
              <span className="text-[10px] font-bold" style={{ color: PRIORITY_META[t.priority]?.color }}>
                {PRIORITY_META[t.priority]?.label}
              </span>
            ) : <span className="text-[10px] text-white/40">—</span> },
          { key: 'assignedTo', label: 'Atribuído', width: '120px', mobileHidden: true,
            cell: (t) => t.assignedToId
              ? <span className="text-xs text-amber-400 truncate">{t.assignedToId}</span>
              : <span className="text-[10px] text-white/40">—</span> },
          { key: 'updatedAt', label: 'Atualizado', width: '150px', mobileHidden: true,
            cell: (t) => (
              <span className="text-[11px] text-white/55 font-mono">
                {new Date(t.updatedAt).toLocaleString('pt-BR')}
              </span>
            ) },
          { key: 'flag', label: '', width: '80px', mobileHidden: true,
            cell: (t) => t.status === 'open' && !t.lastReplyByStaff ? (
              <span className="text-[10px] font-bold text-yellow-400">AGUARDA</span>
            ) : null },
        ]}
        rows={items}
        rowKey={(t) => t.id}
        onRowClick={async (t) => {
          try { setSelected(await getAdminTicket(t.id)); }
          catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
        }}
        loading={loading}
        empty="Nenhum ticket encontrado"
        mobileTitle={(t) => `#${t.id} — ${t.subject}`}
        mobileSubtitle={(t) => `${t.accountId || `UserNum ${t.userNum}`} · ${t.messageCount} msg`}
      />
    </div>
  );
}


// ----------------------------------------------------------------- Detalhe + resposta

function TicketDetailView({
  ticket, onBack, onUpdate, onReload,
}: {
  ticket: TicketDetail;
  onBack: () => void;
  onUpdate: (t: TicketDetail) => void;
  onReload: () => void;
}) {
  const { notify } = useToast();
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const sm = STATUS_META[ticket.status];

  const sendReply = async () => {
    if (body.trim().length < 1) return;
    setBusy(true);
    try {
      const next = await staffReply(ticket.id, { body: body.trim() });
      setBody('');
      onUpdate(next);
      notify('success', 'Resposta enviada');
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (status: TicketStatus) => {
    try {
      await updateTicket(ticket.id, { status });
      const r = await getAdminTicket(ticket.id);
      onUpdate(r);
      onReload();
      notify('success', `Status: ${STATUS_META[status].label}`);
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    }
  };

  const changePriority = async (priority: TicketPriority) => {
    try {
      await updateTicket(ticket.id, { priority });
      const r = await getAdminTicket(ticket.id);
      onUpdate(r);
      notify('success', `Prioridade: ${PRIORITY_META[priority].label}`);
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    }
  };

  const take = async () => {
    try {
      await updateTicket(ticket.id, { take: true });
      const r = await getAdminTicket(ticket.id);
      onUpdate(r);
      notify('success', 'Ticket atribuído a você');
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    }
  };

  return (
    <div>
      <button onClick={onBack}
        className="text-sm text-white/65 hover:text-white flex items-center gap-2 mb-4 cursor-pointer">
        <ArrowLeft size={14} /> Voltar para a lista
      </button>

      <div className="surface-card rounded-xl p-6 mb-4">
        <div className="flex items-start justify-between gap-4 mb-3">
          <h1 className="text-xl md:text-2xl font-bold text-white">
            #{ticket.id} — {ticket.subject}
          </h1>
          <span className="text-[11px] font-bold px-3 py-1 rounded-full"
                style={{ background: sm.bg, color: sm.color }}>
            {sm.label.toUpperCase()}
          </span>
        </div>
        <p className="text-xs text-white/55">
          Aberto em {new Date(ticket.createdAt).toLocaleString('pt-BR')}
          {' · '}última atualização {new Date(ticket.updatedAt).toLocaleString('pt-BR')}
        </p>

        {/* Ações */}
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-white/[0.05]">
          <button onClick={take}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer
                       bg-amber-500/10 border border-amber-500/30 text-amber-400 hover:bg-amber-500/20
                       flex items-center gap-1.5">
            <UserCheck size={12} /> Pegar pra mim
          </button>

          {ticket.status !== 'open' && (
            <button onClick={() => changeStatus('open')}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer
                         bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/20
                         flex items-center gap-1.5">
              <Clock size={12} /> Reabrir
            </button>
          )}
          {ticket.status !== 'answered' && (
            <button onClick={() => changeStatus('answered')}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer
                         bg-[#00e1d7]/10 border border-[#00e1d7]/30 text-[#00e1d7] hover:bg-[#00e1d7]/20
                         flex items-center gap-1.5">
              <CheckCircle size={12} /> Marcar Respondido
            </button>
          )}
          {ticket.status !== 'closed' && (
            <button onClick={() => changeStatus('closed')}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer
                         bg-white/[0.04] border border-white/10 text-white/75 hover:bg-white/[0.08]
                         flex items-center gap-1.5">
              <Lock size={12} /> Fechar
            </button>
          )}

          <div className="flex items-center gap-1 ml-auto">
            <span className="text-[10px] text-white/45 mr-1">PRIORIDADE</span>
            {(['low', 'normal', 'high'] as TicketPriority[]).map(p => (
              <button key={p}
                onClick={() => changePriority(p)}
                className={`px-2 py-1 rounded text-[10px] font-bold cursor-pointer transition
                            ${ticket.priority === p
                              ? 'border'
                              : 'opacity-50 hover:opacity-100'}`}
                style={{
                  borderColor: PRIORITY_META[p].color + '55',
                  background: ticket.priority === p ? PRIORITY_META[p].color + '15' : 'transparent',
                  color: PRIORITY_META[p].color,
                }}>
                {PRIORITY_META[p].label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="surface-card rounded-xl p-6 mb-4">
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
          {ticket.messages.map(m => (
            <div key={m.id}
                 className={`rounded-xl p-4 ${m.isStaff
                   ? 'bg-[#00e1d7]/[0.06] border border-[#00e1d7]/30'
                   : 'bg-white/[0.04] border border-white/[0.06]'}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <span className={`text-[11px] font-bold uppercase tracking-wider
                                  ${m.isStaff ? 'text-[#00e1d7]' : 'text-white/85'}`}>
                  {m.isStaff ? 'STAFF' : 'JOGADOR'}
                  {m.authorId && ` · ${m.authorId}`}
                </span>
                <span className="text-[10px] text-white/45 ml-auto">
                  {new Date(m.createdAt).toLocaleString('pt-BR')}
                </span>
              </div>
              <p className="text-sm text-white/85 whitespace-pre-wrap break-words">{m.body}</p>
              {/* imagens que o jogador anexou pelo site (Cabal Next) -- abre em outra aba */}
              {!!m.attachments?.length && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {m.attachments.map(url => (
                    <a key={url} href={ticketImgSrc(url)} target="_blank" rel="noopener noreferrer"
                       className="block rounded-md overflow-hidden border border-white/15 hover:border-[#00e1d7]/60">
                      <img src={ticketImgSrc(url)} alt="" loading="lazy" className="w-32 h-24 object-cover" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {ticket.status === 'closed' ? (
        <div className="surface-card rounded-xl p-4 flex items-center gap-2">
          <Lock size={14} className="text-white/45" />
          <p className="text-sm text-white/55">
            Ticket fechado. Reabra antes de responder.
          </p>
        </div>
      ) : (
        <div className="surface-card rounded-xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <MessageCircle size={16} className="text-[#00e1d7]" />
            <h3 className="text-sm font-bold text-white/85">Responder como STAFF</h3>
          </div>
          <textarea value={body} onChange={(e) => setBody(e.target.value)}
            rows={4} maxLength={4000}
            placeholder="Escreva a resposta..."
            className="w-full px-4 py-2.5 rounded-lg text-sm bg-white/5 border border-white/10
                       focus:border-[#00e1d7] resize-none placeholder:text-white/30" />
          <div className="flex justify-between items-center mt-3">
            <p className="text-[10px] text-white/35">{body.length}/4000</p>
            <button onClick={sendReply} disabled={busy || body.trim().length < 1}
              className="px-6 py-2.5 rounded-lg bg-[#00e1d7] hover:bg-[#00e1d7]/85
                         text-[#0d141b] font-bold text-sm transition cursor-pointer
                         disabled:opacity-50 flex items-center gap-2">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Enviar Resposta
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
